import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  List,
  ListItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { TargetingCondition } from '../types';
import VisualConditionEditor from './VisualConditionEditor/VisualConditionEditor';
import { TargetingConditionChip } from './common';
import { useUnsavedChangesWarning } from '../hooks';

// Firebase standard colors for conditions
const FIREBASE_CONDITION_COLORS = [
  { label: 'Blue', value: 'BLUE' },
  { label: 'Green', value: 'GREEN' },
  { label: 'Orange', value: 'ORANGE' },
  { label: 'Purple', value: 'PURPLE' },
  { label: 'Red', value: 'RED' },
  { label: 'Teal', value: 'TEAL' },
  { label: 'Indigo', value: 'INDIGO' },
  { label: 'Cyan', value: 'CYAN' },
  { label: 'Pink', value: 'PINK' },
  { label: 'Yellow', value: 'YELLOW' },
  { label: 'Lime', value: 'LIME' },
  { label: 'Brown', value: 'BROWN' },
];

interface TargetingConditionsManagerProps {
  conditions: TargetingCondition[];
  onConditionsChange: (conditions: TargetingCondition[]) => void;
  disabled?: boolean;
}

const TargetingConditionsManager: React.FC<TargetingConditionsManagerProps> = ({
  conditions = [],
  onConditionsChange,
  disabled = false,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);

  // Form state
  const [conditionName, setConditionName] = useState('');
  const [expression, setExpression] = useState('');
  const [tagColor, setTagColor] = useState(FIREBASE_CONDITION_COLORS[0].value); // Default to BLUE

  // Validation state
  const [nameError, setNameError] = useState('');
  // expressionError will now be managed by VisualConditionEditor or reflect backend validation for the whole string
  const [expressionError, setExpressionError] = useState<string | undefined>(undefined);
  const [backendValidationError, setBackendValidationError] = useState<string | null>(null);

  // Track if user has made changes to the condition form
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Unsaved changes warning for the dialog
  const { ConfirmationDialog } = useUnsavedChangesWarning({
    when: dialogOpen && hasUnsavedChanges,
    message:
      'You have unsaved changes to this condition. Are you sure you want to leave without saving?',
    title: 'Unsaved Condition Changes',
  });
  const resetForm = () => {
    setConditionName('');
    setExpression('');
    setTagColor(FIREBASE_CONDITION_COLORS[0].value);
    setNameError('');
    setExpressionError(undefined);
    setBackendValidationError(null);
    setHasUnsavedChanges(false);
  };

  const handleAddClick = () => {
    setEditIndex(null);
    resetForm();
    setDialogOpen(true);
  };

  const handleEditClick = (index: number) => {
    const condition = conditions[index];
    setEditIndex(index);
    setConditionName(condition.name);
    setExpression(condition.expression);
    setTagColor(condition.tag_color);
    setNameError('');
    setExpressionError(undefined);
    setBackendValidationError(null);
    setHasUnsavedChanges(false); // Reset since we're loading existing data
    setDialogOpen(true);
  };

  const handleDeleteClick = (index: number) => {
    const updatedConditions = [...conditions];
    updatedConditions.splice(index, 1);
    onConditionsChange(updatedConditions);
  };
  const handleSave = () => {
    let isValid = true;
    setNameError('');
    setExpressionError(undefined);

    if (!conditionName.trim()) {
      setNameError('Condition name is required');
      isValid = false;
    } else {
      // Check for duplicate name
      const isDuplicate =
        editIndex === null
          ? conditions.some(c => c.name === conditionName.trim())
          : conditions.some((c, i) => i !== editIndex && c.name === conditionName.trim());

      if (isDuplicate) {
        setNameError('Condition name must be unique');
        isValid = false;
      }
    }

    if (!expression.trim()) {
      // This check is still relevant as VisualConditionEditor might produce an empty string.
      setExpressionError('Expression is required');
      isValid = false;
    }

    if (isValid) {
      const newCondition: TargetingCondition = {
        name: conditionName.trim(),
        expression: expression.trim(),
        tag_color: tagColor,
      };

      const updatedConditions = [...conditions];
      if (editIndex !== null) {
        updatedConditions[editIndex] = newCondition;
      } else {
        updatedConditions.push(newCondition);
      }
      onConditionsChange(updatedConditions);
      setDialogOpen(false);
      setHasUnsavedChanges(false);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setHasUnsavedChanges(false);
  };
  // Reset backend validation status when expression changes
  useEffect(() => {
    // When the expression changes, reset the backend validation status
    setBackendValidationError(null);
    setExpressionError(undefined);
  }, [expression]);

  return (
    <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Targeting Conditions
          <Tooltip title="Define Firebase Remote Config targeting conditions. Each condition has a name, a Firebase expression, and a display color.">
            <InfoOutlinedIcon fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
          </Tooltip>
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          disabled={disabled}
          size="small"
        >
          Add Condition
        </Button>
      </Box>
      {conditions.length === 0 && !disabled && (
        <Typography variant="body2" color="text.secondary">
          No targeting conditions defined. Click &quot;Add Condition&quot; to create one.
        </Typography>
      )}
      {conditions.length === 0 && disabled && (
        <Typography variant="body2" color="text.secondary">
          No targeting conditions.
        </Typography>
      )}
      <List dense>
        {conditions.map((condition, index) => (
          <ListItem
            key={condition.name ?? `condition-${index}`}
            divider
            sx={{ mb: 1, p: 1.5, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}
          >
            <Box sx={{ width: '100%' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  width: '100%',
                  mb: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <TargetingConditionChip condition={condition} sx={{ mr: 1 }} size="small" />
                </Box>
                {!disabled && (
                  <Box>
                    <IconButton
                      onClick={e => {
                        e.stopPropagation();
                        handleEditClick(index);
                      }}
                      size="small"
                      aria-label="edit condition"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={e => {
                        e.stopPropagation();
                        handleDeleteClick(index);
                      }}
                      size="small"
                      aria-label="delete condition"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Expression:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    backgroundColor: 'grey.50',
                    p: 1,
                    borderRadius: 1,
                    mt: 0.5,
                  }}
                >
                  {condition.expression}
                </Typography>
              </Box>
            </Box>
          </ListItem>
        ))}
      </List>
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editIndex !== null ? 'Edit Targeting Condition' : 'Add New Targeting Condition'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={8}>
              <TextField
                autoFocus
                margin="dense"
                name="conditionName"
                label="Condition Name"
                type="text"
                fullWidth
                variant="outlined"
                value={conditionName}
                onChange={e => {
                  setConditionName(e.target.value);
                  setHasUnsavedChanges(true);
                }}
                error={!!nameError}
                helperText={nameError || 'Unique name for this condition.'}
                disabled={disabled}
              />
            </Grid>
            <Grid size={4}>
              <FormControl fullWidth margin="dense" disabled={disabled}>
                <InputLabel id="tag-color-label">Tag Color</InputLabel>
                <Select
                  labelId="tag-color-label"
                  value={tagColor}
                  label="Tag Color"
                  onChange={e => {
                    setTagColor(e.target.value);
                    setHasUnsavedChanges(true);
                  }}
                  variant="outlined"
                >
                  {FIREBASE_CONDITION_COLORS.map(color => (
                    <MenuItem key={color.value} value={color.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                          component="span"
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: color.value.toLowerCase(),
                            mr: 1,
                            border: '1px solid grey',
                          }}
                        />
                        {color.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <VisualConditionEditor
            value={expression}
            onChange={newExpression => {
              setExpression(newExpression);
              setHasUnsavedChanges(true);
            }}
            error={expressionError ?? backendValidationError ?? undefined} // Pass down relevant error string
            disabled={disabled}
            placeholder="Define targeting rules visually or switch to text mode."
          />
        </DialogContent>
        <DialogActions sx={{ p: '16px 24px' }}>
          <Button onClick={handleDialogClose} color="secondary" disabled={disabled}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary" disabled={disabled}>
            Save Condition
          </Button>{' '}
        </DialogActions>
      </Dialog>
      <ConfirmationDialog />
    </Paper>
  );
};

export default TargetingConditionsManager;
